<template>
    <v-dialog
        v-model="isOpen"
        fullscreen
        persistent
        :retain-focus="false"
        transition="dialog-bottom-transition"
    >
        <v-card class="problem-dialog-card d-flex flex-column" height="100%">
            <!-- Enhanced Sticky Header with Modern Design -->
            <div class="sticky-header-container elevation-3">
                <v-card-title class="enhanced-header white--text d-flex align-center pa-6">
                    <div class="d-flex align-center">
                        <v-avatar color="white" size="48" class="mr-4">
                            <v-icon color="error" size="28">mdi-alert-circle</v-icon>
                        </v-avatar>
                        <div>
                            <h2 class="text-h4 font-weight-bold mb-1">Problem melden</h2>
                            <p class="text-subtitle-1 mb-0 opacity-90">
                                Wählen Sie den Problemtyp und beschreiben Sie das aufgetretene Problem
                            </p>
                        </div>
                    </div>
                    <v-spacer></v-spacer>
                    <v-btn 
                        icon 
                        large
                        color="white"
                        @click="closeModal"
                        class="ml-4"
                    >
                        <v-icon size="28">mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                
            </div>
            
            <!-- Enhanced Scrollable Content -->
            <v-card-text class="flex-grow-1 overflow-y-auto enhanced-content pa-6">
                <!-- General Problem Report Section -->
                <div class="form-section mb-6">
                    <h3 class="text-h6 font-weight-bold mb-4 d-flex align-center">
                        <v-icon color="error" class="mr-2">mdi-alert-circle-outline</v-icon>
                        Allgemeines Problem melden
                    </h3>
                    
                    <v-row>
                        <v-col cols="12" md="6">
                            <v-select
                                v-model="problemType"
                                :items="problemTypes"
                                label="Was ist das Problem?"
                                class="mb-4"
                                outlined
                                hide-details="auto"
                                color="error"
                                prepend-inner-icon="mdi-tag-outline"
                            >
                                <template v-slot:item="{ item }">
                                    <v-list-item-icon>
                                        <v-icon :color="getProblemTypeColor(item)">{{ getProblemTypeIcon(item) }}</v-icon>
                                    </v-list-item-icon>
                                    <v-list-item-content>
                                        <v-list-item-title>{{ item }}</v-list-item-title>
                                    </v-list-item-content>
                                </template>
                            </v-select>
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-textarea 
                                v-model="problemText" 
                                label="Beschreiben Sie das Problem im Detail..." 
                                outlined
                                auto-grow
                                rows="3"
                                color="error"
                                prepend-inner-icon="mdi-text"
                                counter="500"
                                maxlength="500"
                                hide-details="auto"
                                class="enhanced-textarea"
                            />
                        </v-col>
                    </v-row>
                    
                    <v-btn 
                        @click="reportProblem" 
                        color="error"
                        :disabled="!canReportGeneralProblem"
                        :loading="submitting"
                        class="mt-4"
                    >
                        <v-icon left>mdi-send</v-icon>
                        Problem melden
                    </v-btn>
                </div>

                <v-divider class="my-6"></v-divider>

                <!-- Engraving Error Booking Section -->
                <div class="form-section">
                    <h3 class="text-h6 font-weight-bold mb-4 d-flex align-center">
                        <v-icon color="error" class="mr-2">mdi-package-variant-remove</v-icon>
                        Fehlproduktion ausbuchen
                    </h3>
                    
                    <div v-if="loadingFullOrder" class="loading-container">
                        <v-progress-circular 
                            indeterminate 
                            color="error" 
                            size="64"
                            width="4"
                        ></v-progress-circular>
                        <h4 class="text-subtitle-1 mt-4 font-weight-medium">Bestelldaten werden geladen...</h4>
                        <p class="text-body-2 grey--text">Bitte haben Sie einen Moment Geduld</p>
                    </div>
                    
                    <template v-else-if="fullOrder && fullOrder.billbeeOrder && fullOrder.billbeeOrder.Data && fullOrder.billbeeOrder.Data.OrderItems && fullOrder.billbeeOrder.Data.OrderItems.length > 0">
                        <!-- Product Selection Info -->
                        <div class="d-flex justify-space-between align-center mb-4">
                            <p class="text-body-1 mb-0">
                                Wählen Sie die Produkte mit Fehlern aus und geben Sie den Grund an
                            </p>
                            <v-chip 
                                v-if="selectedEngravingItems.length > 0"
                                color="error" 
                                dark
                                class="px-3"
                            >
                                <v-icon left small>mdi-check-circle</v-icon>
                                {{ selectedEngravingItems.length }} ausgewählt
                            </v-chip>
                            <v-chip 
                                v-else
                                color="grey" 
                                outlined
                                class="px-3"
                            >
                                <v-icon left small>mdi-information-outline</v-icon>
                                Keine Auswahl
                            </v-chip>
                        </div>

                        <!-- Product Grid -->
                        <v-row class="product-grid">
                            <v-col
                                v-for="item in fullOrder.billbeeOrder.Data.OrderItems"
                                :key="item.BillbeeId"
                                cols="12"
                                sm="6"
                                lg="4"
                            >
                                <v-card
                                    @click="$event => toggleEngravingProduct(item, $event)"
                                    class="product-card elevation-2"
                                    :class="{'selected-product-card': isEngravingProductSelected(item)}"
                                    height="100%"
                                >
                                    <!-- Product Header -->
                                    <v-card-title class="product-header pb-3">
                                        <div class="d-flex align-center justify-space-between w-100">
                                            <div class="d-flex align-center flex-grow-1">
                                                <v-avatar 
                                                    :color="isEngravingProductSelected(item) ? 'error' : 'grey'"
                                                    size="32"
                                                    class="mr-3"
                                                >
                                                    <span class="white--text font-weight-bold">{{ item.Quantity }}</span>
                                                </v-avatar>
                                                <div class="flex-grow-1">
                                                    <div class="product-sku text-caption grey--text mb-1">{{ item.Product.SKU }}</div>
                                                    <div class="product-title text-subtitle-2 font-weight-bold">{{ item.Product.Title }}</div>
                                                </div>
                                            </div>
                                            <v-checkbox
                                                :input-value="isEngravingProductSelected(item)"
                                                color="error"
                                                readonly
                                                class="selection-checkbox ma-0 pa-0"
                                                dense
                                                :ripple="false"
                                            ></v-checkbox>
                                        </div>
                                    </v-card-title>
                                    
                                    <!-- Product Content -->
                                    <v-card-text class="pt-0">
                                        <!-- Image Preview -->
                                        <div v-if="hasImageAttribute(item.Attributes)" class="image-preview mb-3">
                                            <v-img
                                                :src="filterImageAttributes(item.Attributes)[0].Value"
                                                height="120"
                                                class="rounded"
                                                contain
                                            >
                                                <template v-slot:placeholder>
                                                    <div class="d-flex align-center justify-center fill-height">
                                                        <v-progress-circular indeterminate color="grey"></v-progress-circular>
                                                    </div>
                                                </template>
                                            </v-img>
                                            <v-chip 
                                                v-if="filterImageAttributes(item.Attributes).length > 1"
                                                small
                                                color="primary"
                                                class="image-count"
                                            >
                                                +{{ filterImageAttributes(item.Attributes).length - 1 }}
                                            </v-chip>
                                        </div>
                                        
                                        <!-- Attributes Preview -->
                                        <div class="attributes-preview">
                                            <template v-if="filterTextAttributes(item.Attributes).length > 0">
                                                <v-simple-table dense class="attribute-mini-list">
                                                    <tbody>
                                                        <tr v-for="(attr, i) in filterTextAttributes(item.Attributes).slice(0, 3)" :key="i">
                                                            <td class="attribute-name pa-1 text-caption">{{ attr.Name }}:</td>
                                                            <td class="attribute-value pa-1 text-caption">{{ attr.Value }}</td>
                                                        </tr>
                                                    </tbody>
                                                </v-simple-table>
                                                <div v-if="filterTextAttributes(item.Attributes).length > 3" class="text-center mt-2">
                                                    <v-chip x-small color="info">
                                                        +{{ filterTextAttributes(item.Attributes).length - 3 }} weitere
                                                    </v-chip>
                                                </div>
                                            </template>
                                            <div v-else class="no-attributes text-center grey--text">
                                                <v-icon small>mdi-information-outline</v-icon>
                                                <div class="text-caption">Keine Personalisierung</div>
                                            </div>
                                        </div>
                                        
                                        <!-- Error Reason Form (when selected) -->
                                        <v-expand-transition>
                                            <div v-if="isEngravingProductSelected(item)" class="error-details-form mt-4 pa-3">
                                                <div class="d-flex align-center mb-3">
                                                    <v-icon color="error" small class="mr-2">mdi-alert-circle</v-icon>
                                                    <span class="text-body-2 font-weight-bold">Fehlergrund angeben:</span>
                                                </div>
                                                <v-select
                                                    :value="getItemErrorReason(item).type"
                                                    @input="val => setItemErrorReason(item, val, getItemErrorReason(item).details)"
                                                    :items="errorTypes"
                                                    label="Fehlertyp"
                                                    outlined
                                                    dense
                                                    hide-details="auto"
                                                    color="error"
                                                    class="mb-3"
                                                    @click.stop
                                                    @mousedown.stop
                                                ></v-select>
                                                <v-text-field
                                                    v-if="getItemErrorReason(item).type === 'Eigener Grund'"
                                                    :value="getItemErrorReason(item).details"
                                                    @input="val => setItemErrorReason(item, getItemErrorReason(item).type, val)"
                                                    label="Beschreibung"
                                                    outlined
                                                    dense
                                                    hide-details="auto"
                                                    color="error"
                                                    @click.stop
                                                    @mousedown.stop
                                                ></v-text-field>
                                            </div>
                                        </v-expand-transition>
                                    </v-card-text>
                                </v-card>
                            </v-col>
                        </v-row>
                        
                        <v-btn 
                            @click="bookEngravingError" 
                            color="error"
                            :disabled="!canReportEngravingProblem"
                            :loading="submitting"
                            class="mt-4"
                        >
                            <v-icon left>mdi-package-down</v-icon>
                            Ausbuchen ({{ selectedEngravingItems.length }})
                        </v-btn>
                    </template>
                    
                    <v-alert type="warning" v-else>
                        <v-icon slot="prepend">mdi-package-remove</v-icon>
                        Keine Produkte in dieser Bestellung gefunden.
                    </v-alert>
                </div>
            </v-card-text>
            
            <v-divider></v-divider>
            
            <!-- Enhanced Sticky Footer -->
            <v-card-actions class="enhanced-footer px-6 py-4">
                <!-- Order Info Section -->
                <v-card class="order-info-card pa-3 mr-4" outlined elevation="0">
                    <div class="d-flex align-center">
                        <v-avatar color="primary" size="40" class="mr-3">
                            <v-icon color="white">mdi-account</v-icon>
                        </v-avatar>
                        <div>
                            <div class="text-subtitle-2 font-weight-bold">
                                {{ getCustomerName() }}
                            </div>
                            <div class="text-caption grey--text d-flex align-center">
                                <v-icon small class="mr-1">mdi-package-variant</v-icon>
                                Bestellung: <span class="font-weight-bold ml-1">{{ getOrderId() }}</span>
                            </div>
                        </div>
                    </div>
                </v-card>
                
                <v-spacer></v-spacer>
                
                <!-- Action Buttons -->
                <div class="d-flex align-center">
                    <v-btn 
                        @click="closeModal" 
                        text 
                        large
                        class="mr-3"
                    >
                        <v-icon left>mdi-close</v-icon>
                        Schließen
                    </v-btn>
                </div>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import ProductionService from '@/services/ProductionService';

export default {
    name: 'ProblemModal',
    props: {
        currentItem: {
            type: Object,
            default: null
        },
        user: {
            type: Object,
            default: null
        },
        currentPacklistItemUserLaser: {
            type: Number,
            default: null
        }
    },
    data() {
        return {
            isOpen: true,
            submitting: false,
            problemType: null,
            problemText: null,
            problemTypes: [
                'Fehlende Produkte',
                'Beschädigte Produkte', 
                'Falsche Produkte',
                'Druckfehler',
                'Systemfehler',
                'Sonstiges'
            ],
            errorTypes: [
                'Eigener Grund',
                'Schräggravur',
                'Importierungsfehler',
                'Doppelt Graviert',
                'Schablone nicht genau',
                'Nicht gravierte Zeichen',
                'Falsches Produkt verwendet',
                'Stornierung nach Gravur',
                'Gravur undeutlich',
                'Muster',
                'Achse nicht eingestellt',
                'Cobot spannt nicht richtig',
                'Zwei Gravuren auf einem Produkt',
                'Fehler bei manueller Gravur',
                'Rechts / Links vertauscht',
                'Ersatzteil wurde benötigt'
            ],
            loadingFullOrder: false,
            fullOrder: null,
            selectedEngravingItems: [],
            errorReasons: {}
        }
    },
    computed: {
        canReportEngravingProblem() {
            return this.selectedEngravingItems.length > 0 && 
                  this.selectedEngravingItems.some(item => this.hasItemErrorReason(item));
        },
        canReportGeneralProblem() {
            return this.problemType && this.problemText && this.problemText.trim().length > 0;
        }
    },
    methods: {
        closeModal() {
            this.isOpen = false;
        },
        getCustomerName() {
            if (!this.currentItem) return 'Kein Kundenname';
            if (!this.currentItem.customer) return 'Kein Kundenname';
            
            const firstName = this.currentItem.customer.firstName || '';
            const lastName = this.currentItem.customer.lastName || '';
            const fullName = `${firstName} ${lastName}`.trim();
            
            return fullName || 'Kein Kundenname';
        },
        getOrderId() {
            if (!this.currentItem) return 'N/A';
            return this.currentItem.orderId || 'N/A';
        },
        getProblemTypeIcon(type) {
            const iconMap = {
                'Fehlende Produkte': 'mdi-package-variant-remove',
                'Beschädigte Produkte': 'mdi-package-variant-closed-remove',
                'Falsche Produkte': 'mdi-package-variant-closed',
                'Druckfehler': 'mdi-printer-alert',
                'Systemfehler': 'mdi-alert-circle',
                'Sonstiges': 'mdi-help-circle'
            };
            return iconMap[type] || 'mdi-alert';
        },
        getProblemTypeColor(type) {
            const colorMap = {
                'Fehlende Produkte': 'orange',
                'Beschädigte Produkte': 'red',
                'Falsche Produkte': 'purple',
                'Druckfehler': 'blue',
                'Systemfehler': 'pink',
                'Sonstiges': 'grey'
            };
            return colorMap[type] || 'error';
        },
        async getFullOrderInformation() {
            this.loadingFullOrder = true;

            try {
                if (!this.currentItem || !this.currentItem.orderId) {
                    console.warn('Cannot fetch order: currentItem or orderId is missing');
                    this.loadingFullOrder = false;
                    return;
                }

                let response = await ProductionService.getMongoOrder(this.currentItem.orderId);

                if (response.status === 200) {
                    this.fullOrder = response.data;
                } else {
                    console.error('Failed to load order:', response);
                }
            } catch (error) {
                console.error('Error fetching order information:', error);
            } finally {
                this.loadingFullOrder = false;
            }
        },
        async reportProblem() {
            if (!this.canReportGeneralProblem) return;
            
            this.submitting = true;
            try {
                let message = this.problemType + ' - ' + this.problemText;
                this.$emit('reportProblem', message);
                // Add small delay for better UX
                setTimeout(() => {
                    this.submitting = false;
                    this.closeModal();
                }, 1000);
            } catch (error) {
                this.submitting = false;
                console.error('Error reporting problem:', error);
            }
        },
        async bookEngravingError() {
            if (!this.canReportEngravingProblem) return;
            
            this.submitting = true;
            try {
                for (const item of this.selectedEngravingItems) {
                    const errorReason = this.getItemErrorReason(item);
                    const reason = errorReason.type ? 
                        `${errorReason.type === 'Eigener Grund' ? 'EG' : errorReason.type}${errorReason.details ? ': ' + errorReason.details : ''}` : 
                        "Kein Grund angegeben";

                    let payload = {
                        sku: item.Product.SKU,
                        name: item.Product.Title,
                        variationId: item.BillbeeId,
                        itemId: item.Product.BillbeeId,
                        isMain: true,
                        isActive: true,
                        image: null,
                        qty: item.Quantity,
                        reason: reason,
                        packerId: this.user.id,
                        producerId: parseInt(this.currentPacklistItemUserLaser)
                    }

                    let response = await ProductionService.createDamagedItem2(payload);
                    if (response.status !== 200) {
                        console.error('Failed to create item');
                    }
                }

                this.$emit('refreshPacklist');
                // Add small delay for better UX
                setTimeout(() => {
                    this.submitting = false;
                    this.closeModal();
                }, 1000);
            } catch (error) {
                this.submitting = false;
                console.error(error);
            }
        },
        toggleEngravingProduct(item, event) {
            if (event && 
                (event.target.closest('.v-select') || 
                 event.target.closest('.v-text-field') || 
                 event.target.closest('.error-details-form'))) {
                return;
            }
            
            const index = this.selectedEngravingItems.findIndex(i => i.BillbeeId === item.BillbeeId);
            
            if (index > -1) {
                this.selectedEngravingItems.splice(index, 1);
            } else {
                this.selectedEngravingItems.push(item);
            }
        },
        isEngravingProductSelected(item) {
            return this.selectedEngravingItems.some(i => i.BillbeeId === item.BillbeeId);
        },
        filterTextAttributes(attributes) {
            return attributes.filter(attr => 
                attr.Name && 
                attr.Value && 
                !attr.Value.startsWith('http')
            );
        },
        filterImageAttributes(attributes) {
            return attributes.filter(attr => 
                attr.Value && 
                attr.Value.startsWith('http')
            );
        },
        hasImageAttribute(attributes) {
            return attributes.some(attr => 
                attr.Value && 
                attr.Value.startsWith('http')
            );
        },
        setItemErrorReason(item, type, details) {
            if (!this.errorReasons[item.BillbeeId]) {
                this.$set(this.errorReasons, item.BillbeeId, { type: null, details: null });
            }
            
            this.$set(this.errorReasons[item.BillbeeId], 'type', type);
            this.$set(this.errorReasons[item.BillbeeId], 'details', details);
        },
        getItemErrorReason(item) {
            return this.errorReasons[item.BillbeeId] || { type: null, details: null };
        },
        hasItemErrorReason(item) {
            return this.errorReasons[item.BillbeeId] && 
                  (this.errorReasons[item.BillbeeId].type || this.errorReasons[item.BillbeeId].details);
        }
    },
    watch: {
        isOpen(newVal) {
            if (newVal) {
                // Reset selections when dialog opens
                this.selectedEngravingItems = [];
                this.errorReasons = {};
                this.problemType = null;
                this.problemText = null;
                this.getFullOrderInformation();
            } else {
                this.$emit('close-modal');
            }
        }
    }
}
</script>

<style scoped>
.problem-dialog-card {
    display: flex;
    flex-direction: column;
    height: 100vh;
}

.sticky-header-container {
    position: sticky;
    top: 0;
    z-index: 10;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.enhanced-header {
    background: linear-gradient(135deg, #f44336 0%, #d32f2f 100%);
    position: relative;
    z-index: 10;
}


.enhanced-content {
    background-color: #fafafa;
    min-height: 0;
    scroll-behavior: smooth;
}

.enhanced-content::-webkit-scrollbar {
    width: 8px;
}

.enhanced-content::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
}

.enhanced-content::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
}

.enhanced-content::-webkit-scrollbar-thumb:hover {
    background: #a1a1a1;
}

.form-section {
    background-color: white;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}


.loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 300px;
}

.selection-header {
    background-color: white;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}

.selection-stats {
    min-width: 150px;
    text-align: right;
}

.product-grid {
    margin: 0 -8px;
}

.product-card {
    height: 100%;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border-radius: 12px;
    border-left: 4px solid transparent;
    overflow: hidden;
    cursor: pointer;
    position: relative;
    border: 1px solid rgba(0,0,0,0.08);
}

.product-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0,0,0,0.12) !important;
    border-color: rgba(0,0,0,0.1);
}

.selected-product-card {
    border-left: 4px solid #f44336;
    background: linear-gradient(135deg, rgba(244, 67, 54, 0.05) 0%, rgba(244, 67, 54, 0.02) 100%);
    box-shadow: 0 4px 16px rgba(244, 67, 54, 0.2) !important;
    border-color: rgba(244, 67, 54, 0.3);
}

.selected-product-card::before {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 0 24px 24px 0;
    border-color: transparent #f44336 transparent transparent;
    z-index: 1;
}

.product-header {
    background-color: rgba(0,0,0,0.02);
    border-bottom: 1px solid rgba(0,0,0,0.05);
    position: relative;
}

.product-sku {
    font-family: 'Roboto Mono', monospace;
    background-color: #f5f5f5;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 500;
}

.product-title {
    line-height: 1.3;
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.image-preview {
    position: relative;
    border-radius: 8px;
    overflow: hidden;
    background-color: #f5f5f5;
    border: 1px solid rgba(0,0,0,0.05);
}

.image-count {
    position: absolute;
    bottom: 8px;
    right: 8px;
    z-index: 1;
}

.attributes-preview {
    background-color: #f9f9f9;
    border-radius: 8px;
    padding: 12px;
    min-height: 80px;
    border: 1px solid rgba(0,0,0,0.05);
    position: relative;
}

.attribute-mini-list {
    background-color: transparent !important;
}

.attribute-name {
    font-weight: 500;
    color: #616161;
    font-size: 12px;
    width: 40%;
    padding: 2px 4px;
}

.attribute-value {
    color: #212121;
    font-size: 12px;
    word-break: break-word;
    padding: 2px 4px;
}

.no-attributes {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 60px;
    font-style: italic;
    color: rgba(0,0,0,0.5);
}

.error-details-form {
    background: linear-gradient(135deg, rgba(244, 67, 54, 0.08) 0%, rgba(244, 67, 54, 0.04) 100%);
    border: 1px solid rgba(244, 67, 54, 0.2);
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(244, 67, 54, 0.1);
}

.enhanced-footer {
    position: sticky;
    bottom: 0;
    background-color: white;
    z-index: 10;
    box-shadow: 0 -4px 12px rgba(0,0,0,0.1);
    display: flex;
    align-items: center;
}

.order-info-card {
    flex-shrink: 0;
    border-radius: 8px;
    background: linear-gradient(135deg, #f5f5f5 0%, #eeeeee 100%);
    border: 1px solid rgba(0,0,0,0.05);
}

.enhanced-textarea ::v-deep .v-textarea textarea {
    line-height: 1.5;
}

.selection-checkbox {
    transform: scale(1.3);
}

/* Rotating animation for loading icon */
@keyframes rotating {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}

.rotating {
    animation: rotating 2s linear infinite;
}

/* Smooth transitions */
.v-enter-active, .v-leave-active {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.v-enter, .v-leave-to {
    opacity: 0;
    transform: translateY(20px);
}

/* Enhanced button hover effects */
.v-btn:not(.v-btn--disabled):hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
    transition: all 0.2s ease;
}

/* Enhanced form field focus states */
.v-text-field--outlined.error--text ::v-deep .v-input__control .v-input__slot {
    border-color: #f44336;
}

.v-select--outlined.error--text ::v-deep .v-input__control .v-input__slot {
    border-color: #f44336;
}

/* Improved chip styling */
.v-chip--large {
    font-size: 16px;
    height: 40px;
}

/* Card elevation on hover */
.form-section:hover,
.order-info-card:hover {
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
    transition: box-shadow 0.3s ease;
}

/* Enhanced progress circular */
.v-progress-circular {
    filter: drop-shadow(0 2px 4px rgba(0,0,0,0.1));
}

/* Improved list item hover */
.v-list-item:hover {
    background-color: rgba(0,0,0,0.02);
}

/* Better focus indicators */
.v-btn:focus::before {
    opacity: 0.12;
}

.v-text-field:focus ::v-deep .v-input__slot::before,
.v-select:focus ::v-deep .v-input__slot::before {
    border-color: #f44336;
    border-width: 2px;
}

/* Enhanced avatar styling */
.v-avatar {
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

/* Responsive design improvements */
@media (max-width: 960px) {
    .enhanced-header {
        padding: 16px !important;
    }
    
    .enhanced-content {
        padding: 16px !important;
    }
    
    .enhanced-footer {
        padding: 16px !important;
        flex-direction: column;
        gap: 16px;
    }
    
    .order-info-card {
        margin-right: 0 !important;
        margin-bottom: 16px;
        width: 100%;
    }
    
    .product-grid .v-col {
        padding: 8px;
    }
}

@media (max-width: 600px) {
    .text-h4 {
        font-size: 1.5rem !important;
    }
    
    .text-h5 {
        font-size: 1.25rem !important;
    }
    
    .form-section {
        padding: 16px;
    }
}
</style>
